/*eslint-disable*/
import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import IconButton from "@material-ui/core/IconButton";
import FiberManualRecord from "@material-ui/icons/FiberManualRecord";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";

import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";

import CardFooter from "../../components/Card/CardFooter";
import CardText from "../../components/Card/CardText.js";

import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  pageSubcategoriesTitle: {
    color: "#3C4858",
    textDecoration: "none",
    textAlign: "center",
    fontSize: "1rem",
  },

  cardCategory: {
    margin: 0,
    color: "#999999",
    fontSize: "1rem",
  },

  cardRoot: {
    display: "flex",
    flexDirection: "column",
  },

  cardTitle: {
    marginTop: 0,
    minHeight: "auto",
    fontSize: "1rem",
    marginBottom: 0,
    wordBreak: "break-word",
    overflowWrap: "anywhere",

    [theme.breakpoints.down("sm")]: {
      fontSize: "0.95rem",
    },
  },

  cardTextHead: {
    background: "#fafafa",
    borderBottom: "1px solid #e0e0e0",
    padding: theme.spacing(1, 1.5),

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1, 1.25),
    },
  },

  compactBody: {
    padding: "0px 16px 16px !important",
    marginTop: theme.spacing(2),

    [theme.breakpoints.down("sm")]: {
      padding: "0px 12px 12px !important",
      marginTop: theme.spacing(1.5),
    },
  },

  expandContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    width: "100%",
    cursor: "pointer",
    padding: 0,
    background: "transparent",
    border: 0,
    textAlign: "left",
    color: "inherit",

    "& h4": {
      margin: 0,
      fontSize: "1rem",
      wordBreak: "break-word",
      overflowWrap: "anywhere",
    },

    [theme.breakpoints.down("sm")]: {
      "& h4": {
        fontSize: "0.95rem",
      },
    },
  },

  expandText: {
    flex: 1,
    minWidth: 0,
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },

  iconButton: {
    flexShrink: 0,
    padding: theme.spacing(0.5),
  },

  listWrapper: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
  },

  listItem: {
    fontSize: "0.85rem",
    lineHeight: 1.5,
    padding: theme.spacing(0.75, 0),
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    minWidth: 0,
    wordBreak: "break-word",
    overflowWrap: "anywhere",

    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },

  bulletIcon: {
    fontSize: 8,
    marginTop: 6,
    flexShrink: 0,
  },

  itemText: {
    minWidth: 0,
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    whiteSpace: "normal",
  },

  emptyState: {
    fontSize: "0.85rem",
    color: "#666",
    padding: theme.spacing(0.5, 0),
  },
}));

const formatCategoryKey = (key) => {
  if (!key) return "";
  const withoutUnderscores = key.replace(/_/g, " ");
  return withoutUnderscores.charAt(0).toUpperCase() + withoutUnderscores.slice(1);
};

const normalizeItems = (data) => {
  if (!Array.isArray(data)) return [];
  return data
    .filter((item) => item !== null && item !== undefined && String(item).trim() !== "")
    .map((item) => String(item));
};

const SimpleCardInfo = ({ data, categoryKey }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation("search");

  const items = normalizeItems(data);

  if (!Array.isArray(data)) {
    return null;
  }

  const categoryKeyForI18n = categoryKey ? categoryKey.replace(/_/g, "-") : "";
  const toggleExpanded = () => setExpanded((prev) => !prev);

  return (
    <GridItem xs={12} sm={6} md={4}>
      <Card className={classes.cardRoot}>
        <CardText className={classes.cardTextHead}>
          <h4 className={classes.cardTitle}>
            {t(`simplelist-category-${categoryKeyForI18n}`, {
              defaultValue: formatCategoryKey(categoryKey),
            })}
          </h4>
        </CardText>

        <CardBody className={classes.compactBody}>
          <button
            type="button"
            className={classes.expandContainer}
            onClick={toggleExpanded}
            aria-expanded={expanded}
            aria-label={t("simplelist-view-items", { count: items.length })}
          >
            <Typography component="h4" className={classes.expandText}>
              {t("simplelist-view-items", { count: items.length })}
            </Typography>

            <IconButton
              className={classes.iconButton}
              aria-hidden="true"
              tabIndex={-1}
            >
              <ExpandMoreIcon
                style={{
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </IconButton>
          </button>

          {expanded ? (
            <Box className={classes.listWrapper}>
              {items.length > 0 ? (
                items.map((item, index) => (
                  <div key={`${categoryKey}-${index}`} className={classes.listItem}>
                    <FiberManualRecord className={classes.bulletIcon} />
                    <span className={classes.itemText}>{item}</span>
                  </div>
                ))
              ) : (
                <Typography className={classes.emptyState}>
                  {t("metainfo-no-records", { defaultValue: "No data available." })}
                </Typography>
              )}
            </Box>
          ) : null}
        </CardBody>
      </Card>
    </GridItem>
  );
};

SimpleCardInfo.propTypes = {
  data: PropTypes.array.isRequired,
  categoryKey: PropTypes.string.isRequired,
};

const SimpleListInfo = ({ record }) => {
  const allowedKeys = [
    "activities",
    "receivers",
    "providers",
    "services",
    "libraries",
    "secrets",
    "exported_activities",
  ];

  if (!record || typeof record !== "object") {
    return null;
  }

  return Object.entries(record)
    .filter(([key, value]) => allowedKeys.includes(key) && Array.isArray(value))
    .map(([key, value]) => (
      <SimpleCardInfo key={key} data={value} categoryKey={key} />
    ));
};

SimpleListInfo.propTypes = {
  record: PropTypes.object.isRequired,
};

export default SimpleListInfo;